import { useCallback, useEffect, useState } from 'react';
import { submitAgriTour } from '@/lib/api/agritour';
import { saveAgriTourDraft, setStoredAgriTourDraftToken } from '@/lib/api/agritour-draft';
import {
  AgriTourClicksFooter,
  AgriTourSiteHeader,
  AgriTourWelcome,
  AGRITOUR_FORM_TITLE,
  AGRITOUR_SECTIONS,
  AGRITOUR_STEP_LABELS,
} from './AgriTourChrome';
import { AgriTourDraftModal } from './AgriTourDraftModal';
import AgriTourStepA from './AgriTourStepA';
import AgriTourStepB from './AgriTourStepB';
import AgriTourStepC from './AgriTourStepC';
import AgriTourStepper from './AgriTourStepper';
import AgriTourStepD from './AgriTourStepD';
import AgriTourSuccess from './AgriTourSuccess';
import { validateAgriTourStep } from './agritour-validation';
import { AGRITOUR_DRAFT_COPY } from './agritour-form-copy';
import {
  canSaveAgriTourDraft,
  useAgriTourDraftResume,
  useAgriTourDraftToken,
} from './useAgriTourDraft';
import styles from './AgriTourScreen.module.css';

export type AgriTourStep = 'A' | 'B' | 'C' | 'D';

const STEPS: { id: AgriTourStep; title: string }[] = [
  { id: 'A', title: AGRITOUR_STEP_LABELS[0] },
  { id: 'B', title: AGRITOUR_STEP_LABELS[1] },
  { id: 'C', title: AGRITOUR_STEP_LABELS[2] },
  { id: 'D', title: AGRITOUR_STEP_LABELS[3] },
];

export type AgriTourFormData = {
  // Step A
  applicantName: string;
  idNumber: string;
  legalEntity: string;
  legalEntityOther: string;
  ventureName: string;
  settlementItemId: string;
  contactPerson: string;
  phone: string;
  email: string;
  miluimActive: string;
  ventureDescription: string;
  // Step B
  lat: string;
  lng: string;
  selectionMode: string;
  polygonJson: string;
  coordX: string;
  coordY: string;
  gushNumber: string;
  helkaNumber: string;
  meshekNumber: string;
  ownershipStatus: string;
  ownershipStatusOther: string;
  parcelType: string;
  leaseAgreementRMI: string;
  rentalAgreementVillage: string;
  fiveYearAgreement: string;
  statutoryMaturity: string;
  nearbyPlans: string;
  environmentalPlansDetail: string;
  touristAnchors: string;
  touristAnchorsDetail: string;
  // Step C
  ventureLocation: string;
  ventureActivityStatus: string;
  readinessLevel: string;
  agriculturalIntegration: string;
  agriculturalDetail: string;
  buildingElements: number[];
  ventureAreaSqm: string;
  numEmployees: string;
  hasPhysicalInfrastructure: string;
  requiredConnections: number[];
  shadedGatheringArea: string;
  sanitationSolution: string;
  hasStatutoryAccessRoad: string;
  accessibilityType: string;
  hasOrganizedParking: string;
  existingParkingSpots: string;
  needsNewParking: string;
  parkingCarsNeeded: string;
  parkingBusesNeeded: string;
  seasonalOrPermanent: string;
  activitySeasons: string;
  activityHours: string;
  nightActivity: string;
  outdoorLighting: string;
  amplificationSystem: string;
  avgVisitorsPerDay: string;
  simultaneousCapacity: string;
  inboundTourism: string;
  additionalAudiences: string;
  additionalAudiencesCheckbox: boolean;
};

export type AgriTourFiles = {
  filezgshkkm2: File | null;
  filev6ow2t9a: File | null;
  filemk54lt2a: File | null;
  file4khnuexa: File | null;
  fileki8tq148: File | null;
  file_mm4m76fa: File | null;
  file_mm4mw3k7: File[];
  file9djsb71e: File[];
  signaturecrvya9ht: File | null;
};

const DRAFT_KEY = 'agritour_draft';

const INITIAL_FORM_DATA: AgriTourFormData = {
  applicantName: '',
  idNumber: '',
  legalEntity: '',
  legalEntityOther: '',
  ventureName: '',
  settlementItemId: '',
  contactPerson: '',
  phone: '',
  email: '',
  miluimActive: '',
  ventureDescription: '',
  lat: '',
  lng: '',
  selectionMode: '',
  polygonJson: '',
  coordX: '',
  coordY: '',
  gushNumber: '',
  helkaNumber: '',
  meshekNumber: '',
  ownershipStatus: '',
  ownershipStatusOther: '',
  parcelType: '',
  leaseAgreementRMI: '',
  rentalAgreementVillage: '',
  fiveYearAgreement: '',
  statutoryMaturity: '',
  nearbyPlans: '',
  environmentalPlansDetail: '',
  touristAnchors: '',
  touristAnchorsDetail: '',
  ventureLocation: '',
  ventureActivityStatus: '',
  readinessLevel: '',
  agriculturalIntegration: '',
  agriculturalDetail: '',
  buildingElements: [],
  ventureAreaSqm: '',
  numEmployees: '',
  hasPhysicalInfrastructure: '',
  requiredConnections: [],
  shadedGatheringArea: '',
  sanitationSolution: '',
  hasStatutoryAccessRoad: '',
  accessibilityType: '',
  hasOrganizedParking: '',
  existingParkingSpots: '',
  needsNewParking: '',
  parkingCarsNeeded: '',
  parkingBusesNeeded: '',
  seasonalOrPermanent: '',
  activitySeasons: '',
  activityHours: '',
  nightActivity: '',
  outdoorLighting: '',
  amplificationSystem: '',
  avgVisitorsPerDay: '',
  simultaneousCapacity: '',
  inboundTourism: '',
  additionalAudiences: '',
  additionalAudiencesCheckbox: false,
};

const INITIAL_FILES: AgriTourFiles = {
  filezgshkkm2: null,
  filev6ow2t9a: null,
  filemk54lt2a: null,
  file4khnuexa: null,
  fileki8tq148: null,
  file_mm4m76fa: null,
  file_mm4mw3k7: [],
  file9djsb71e: [],
  signaturecrvya9ht: null,
};

function loadDraft(): AgriTourFormData {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return INITIAL_FORM_DATA;
    return { ...INITIAL_FORM_DATA, ...(JSON.parse(raw) as Partial<AgriTourFormData>) };
  } catch {
    return INITIAL_FORM_DATA;
  }
}

function hasDraft(): boolean {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<AgriTourFormData>;
    return Boolean(parsed.applicantName?.trim() || parsed.ventureName?.trim());
  } catch {
    return false;
  }
}

export default function AgriTourScreen() {
  const { draftToken, setDraftToken } = useAgriTourDraftToken();
  const [showWelcome, setShowWelcome] = useState(() => !hasDraft());
  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState<AgriTourFormData>(loadDraft);
  const [files, setFiles] = useState<AgriTourFiles>(INITIAL_FILES);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [stepAlert, setStepAlert] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftModal, setDraftModal] = useState<{ resumeUrl: string; expiresAt: string } | null>(
    null,
  );

  const handleDraftResume = useCallback(
    (answers: Partial<AgriTourFormData>, resumedStepIndex: number, token: string) => {
      setFormData({ ...INITIAL_FORM_DATA, ...answers });
      setStepIndex(Math.min(Math.max(resumedStepIndex, 0), STEPS.length - 1));
      setDraftToken(token);
      setShowWelcome(false);
    },
    [setDraftToken],
  );

  const { loadingDraft, draftLoadError } = useAgriTourDraftResume({
    onResume: handleDraftResume,
  });

  const currentStep = STEPS[stepIndex];
  const stepSection =
    currentStep.id === 'A'
      ? AGRITOUR_SECTIONS.a
      : currentStep.id === 'B'
        ? AGRITOUR_SECTIONS.b
        : currentStep.id === 'C'
          ? AGRITOUR_SECTIONS.c
          : AGRITOUR_SECTIONS.d;
  const stepIntro =
    currentStep.id === 'B'
      ? AGRITOUR_SECTIONS.b.intro
      : currentStep.id === 'C'
        ? AGRITOUR_SECTIONS.c.intro
        : null;

  // Persist non-file fields to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    } catch {
      // ignore storage errors
    }
  }, [formData]);

  const updateFormData = useCallback((patch: Partial<AgriTourFormData>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) {
        delete next[key];
      }
      return next;
    });
    setStepAlert(null);
  }, []);

  const updateFile = useCallback((key: keyof AgriTourFiles, file: File | null) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setStepAlert(null);
  }, []);

  const addVisualAppendixFiles = useCallback((picked: File[]) => {
    if (picked.length === 0) return;
    setFiles((prev) => ({
      ...prev,
      file9djsb71e: [...prev.file9djsb71e, ...picked],
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.file9djsb71e;
      return next;
    });
    setStepAlert(null);
  }, []);

  const removeVisualAppendixFile = useCallback((index: number) => {
    setFiles((prev) => ({
      ...prev,
      file9djsb71e: prev.file9djsb71e.filter((_, i) => i !== index),
    }));
  }, []);

  const addProjectPlansFiles = useCallback((picked: File[]) => {
    if (picked.length === 0) return;
    setFiles((prev) => ({
      ...prev,
      file_mm4mw3k7: [...prev.file_mm4mw3k7, ...picked],
    }));
    setStepAlert(null);
  }, []);

  const removeProjectPlansFile = useCallback((index: number) => {
    setFiles((prev) => ({
      ...prev,
      file_mm4mw3k7: prev.file_mm4mw3k7.filter((_, i) => i !== index),
    }));
  }, []);

  const runStepValidation = useCallback(
    (stepId: AgriTourStep) => validateAgriTourStep(stepId, formData, files),
    [formData, files],
  );

  const handleNext = useCallback(() => {
    const result = runStepValidation(currentStep.id);
    if (!result.valid) {
      setFieldErrors(result.fieldErrors);
      setStepAlert(result.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setFieldErrors({});
    setStepAlert(null);
    if (stepIndex < STEPS.length - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0 });
    }
  }, [stepIndex, currentStep.id, runStepValidation]);

  const handleBack = useCallback(() => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      window.scrollTo({ top: 0 });
    }
  }, [stepIndex]);

  const handleSaveDraft = useCallback(async () => {
    if (!canSaveAgriTourDraft(formData)) {
      setStepAlert(AGRITOUR_DRAFT_COPY.minFieldsError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSavingDraft(true);
    setStepAlert(null);
    try {
      const saved = await saveAgriTourDraft({
        answers: formData as unknown as Record<string, unknown>,
        stepIndex,
        draftToken,
      });
      setDraftToken(saved.draftToken);
      setDraftModal({ resumeUrl: saved.resumeUrl, expiresAt: saved.expiresAt });
    } catch (err) {
      setStepAlert(err instanceof Error ? err.message : AGRITOUR_DRAFT_COPY.saveError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSavingDraft(false);
    }
  }, [draftToken, formData, setDraftToken, stepIndex]);

  const handleSubmit = useCallback(async () => {
    const result = runStepValidation('D');
    if (!result.valid) {
      setFieldErrors(result.fieldErrors);
      setStepAlert(result.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const fd = new FormData();
      const payload = {
        ...formData,
        additionalAudiencesCheckbox: formData.additionalAudiences === '0',
      };
      for (const [key, value] of Object.entries(payload)) {
        if (value === '' || value === null || value === undefined) continue;
        if (Array.isArray(value)) {
          if (value.length === 0) continue;
          fd.append(key, JSON.stringify(value));
        } else {
          fd.append(key, String(value));
        }
      }
      for (const [key, value] of Object.entries(files)) {
        if ((key === 'file9djsb71e' || key === 'file_mm4mw3k7') && Array.isArray(value)) {
          for (const file of value) {
            fd.append(key, file, file.name);
          }
          continue;
        }
        const file = value as File | null;
        if (file) {
          fd.append(key, file, file.name);
        }
      }
      if (draftToken) {
        fd.append('draftToken', draftToken);
      }
      await submitAgriTour(fd);
      localStorage.removeItem(DRAFT_KEY);
      setStoredAgriTourDraftToken(null);
      setDraftToken(null);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'שגיאה בשליחת הטופס');
    } finally {
      setSubmitting(false);
    }
  }, [draftToken, formData, files, runStepValidation, setDraftToken]);

  if (loadingDraft) {
    return (
      <div className={styles.page} dir="rtl" lang="he">
        <AgriTourSiteHeader title={AGRITOUR_FORM_TITLE} />
        <div className={styles.loading}>
          <div className={styles.loadingInner}>
            <span className={styles.spinner} />
            <p className={styles.loadingLabel}>{AGRITOUR_DRAFT_COPY.loadingDraft}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return <AgriTourSuccess />;
  }

  if (showWelcome) {
    return <AgriTourWelcome onStart={() => setShowWelcome(false)} />;
  }

  return (
    <div className={styles.page} dir="rtl" lang="he">
      <AgriTourSiteHeader title={AGRITOUR_FORM_TITLE} />
      <div className={styles.container}>
        <div className={styles.formShell}>
          <AgriTourStepper steps={STEPS} activeIndex={stepIndex} embedded />

          <main className={`${styles.formBody} ${styles.formBodyEmbedded}`}>
          <header className={styles.stepHeader}>
            <p className={styles.stepEyebrow}>{currentStep.title}</p>
            <h2 className={styles.stepHeading}>{stepSection.title}</h2>
            {stepIntro ? <p className={styles.stepLead}>{stepIntro}</p> : null}
          </header>

          {stepAlert && (
            <p className={styles.stepAlert} role="alert">
              {stepAlert}
            </p>
          )}
          {draftLoadError && (
            <p className={styles.stepAlert} role="alert">
              {draftLoadError}
            </p>
          )}
          {currentStep.id === 'A' && (
            <AgriTourStepA
              formData={formData}
              files={files}
              fieldErrors={fieldErrors}
              onChange={updateFormData}
              onFileChange={updateFile}
            />
          )}
          {currentStep.id === 'B' && (
            <AgriTourStepB
              formData={formData}
              files={files}
              fieldErrors={fieldErrors}
              onChange={updateFormData}
              onFileChange={updateFile}
            />
          )}
          {currentStep.id === 'C' && (
            <AgriTourStepC
              formData={formData}
              files={files}
              fieldErrors={fieldErrors}
              onChange={updateFormData}
              onProjectPlansAdd={addProjectPlansFiles}
              onProjectPlansRemove={removeProjectPlansFile}
            />
          )}
          {currentStep.id === 'D' && (
            <AgriTourStepD
              files={files}
              fieldErrors={fieldErrors}
              onFileChange={updateFile}
              onVisualAppendixAdd={addVisualAppendixFiles}
              onVisualAppendixRemove={removeVisualAppendixFile}
            />
          )}

          {submitError && (
            <p className={styles.submitError} role="alert">
              {submitError}
            </p>
          )}

          <div className={styles.navBar}>
            <div className={styles.navBarStart}>
              {stepIndex > 0 && (
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={handleBack}
                  disabled={submitting || savingDraft}
                >
                  הקודם
                </button>
              )}
              <button
                type="button"
                className={styles.btnDraft}
                onClick={() => void handleSaveDraft()}
                disabled={submitting || savingDraft}
              >
                {savingDraft ? 'שומר...' : AGRITOUR_DRAFT_COPY.saveButton}
              </button>
            </div>
            {stepIndex < STEPS.length - 1 ? (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleNext}
                disabled={submitting || savingDraft}
              >
                הבא
              </button>
            ) : (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => void handleSubmit()}
                disabled={submitting || savingDraft}
              >
                {submitting ? 'שולח...' : 'להגשה'}
              </button>
            )}
          </div>
        </main>
        </div>
      </div>
      {draftModal ? (
        <AgriTourDraftModal
          resumeUrl={draftModal.resumeUrl}
          expiresAt={draftModal.expiresAt}
          onClose={() => setDraftModal(null)}
        />
      ) : null}
      <AgriTourClicksFooter />
    </div>
  );
}
